import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  updateProfile,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { setSentryUser, clearSentryUser } from '@/monitoring/sentry';
import { flushPerformanceMetrics } from '@/lib/monitoring/performance';

// ============================================================================
// IDLE TIMEOUT CONFIGURATION
// ============================================================================
// Option A: Time-based inactivity model (not browser-close detection)
// - User stays logged in across tabs and browser restarts
// - Session ends via idle timeout, not browser-close heuristics
// - "Remember me" extends the idle timeout
// ============================================================================

const IDLE_TIMEOUT_DEFAULT = 12 * 60 * 60 * 1000; // 12 hours (in ms)
const IDLE_TIMEOUT_REMEMBER_ME = 30 * 24 * 60 * 60 * 1000; // 30 days (in ms)
const IDLE_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds
const ACTIVITY_KEY = 'modcar_last_activity';
const REMEMBER_ME_KEY = 'modcar_remember_me';

/**
 * Get the idle timeout based on remember me preference
 */
function getIdleTimeout(): number {
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  return rememberMe ? IDLE_TIMEOUT_REMEMBER_ME : IDLE_TIMEOUT_DEFAULT;
}

/**
 * Update the last activity timestamp
 */
function updateLastActivity(): void {
  localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
}

/**
 * Check if the session has expired due to inactivity
 */
function isSessionExpired(): boolean {
  const lastActivity = localStorage.getItem(ACTIVITY_KEY);
  if (!lastActivity) return false; // No activity recorded yet, don't expire

  const lastActivityTime = parseInt(lastActivity, 10);
  const now = Date.now();
  const idleTimeout = getIdleTimeout();

  return now - lastActivityTime > idleTimeout;
}

/**
 * Result from OAuth sign-in with isNewUser detection
 */
export interface OAuthSignInResult {
  user: User;
  isNewUser: boolean;
  providerId: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /**
   * Sign in with Google (for LOGIN page - existing users only)
   * Returns isNewUser so caller can handle new user case appropriately
   */
  signInWithGoogle: () => Promise<OAuthSignInResult>;
  /**
   * Sign in with Microsoft (for LOGIN page - existing users only)
   * Returns isNewUser so caller can handle new user case appropriately
   */
  signInWithMicrosoft: () => Promise<OAuthSignInResult>;
  /** Force refresh the auth token (useful before sensitive operations) */
  refreshToken: () => Promise<string | null>;
  /**
   * True if OAuth redirect detected a new user (for routing to team creation)
   * Null if redirect hasn't been processed yet
   */
  oauthRedirectNewUser: boolean | null;
  /** Clear the OAuth redirect new user flag after handling */
  clearOAuthRedirectNewUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track if this is a new OAuth user from redirect (for routing)
  const [oauthRedirectNewUser, setOauthRedirectNewUser] = useState<boolean | null>(null);

  // Handle redirect result (for Safari/blocked popup fallback)
  // FIX H1: Extract isNewUser from redirect result for proper routing
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          const additionalInfo = getAdditionalUserInfo(result);
          const isNewUser = additionalInfo?.isNewUser ?? false;
          const providerId = result.providerId || 'unknown';

          logger.info('[Auth] OAuth sign-in successful via redirect', {
            isNewUser,
            providerId,
            email: result.user.email,
          });

          // Update activity on successful OAuth redirect
          updateLastActivity();

          // Store isNewUser state for routing decisions
          // The SignUpPage or LoginPage will check this and route accordingly
          if (isNewUser) {
            setOauthRedirectNewUser(true);
            // Store in sessionStorage so it persists across the redirect
            sessionStorage.setItem('oauth_redirect_new_user', 'true');
          } else {
            setOauthRedirectNewUser(false);
            sessionStorage.removeItem('oauth_redirect_new_user');
          }
        } else {
          // Check if we have a pending new user flag from a previous redirect
          const pendingNewUser = sessionStorage.getItem('oauth_redirect_new_user');
          if (pendingNewUser === 'true') {
            setOauthRedirectNewUser(true);
          }
        }
      } catch (error) {
        logger.error('[Auth] Redirect result error:', error);
        sessionStorage.removeItem('oauth_redirect_new_user');
      }
    };
    void handleRedirectResult();
  }, []);

  // ============================================================================
  // OPTION A: Idle Timeout Model
  // ============================================================================
  // - Always use browserLocalPersistence (shared across tabs)
  // - Check idle timeout on auth state change
  // - NO sessionStorage hacks, NO browser-close detection
  // ============================================================================
  useEffect(() => {
    let hasInitialized = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Initialize persistence once
      if (!hasInitialized) {
        hasInitialized = true;

        try {
          // Always use local persistence for cross-tab sharing
          await setPersistence(auth, browserLocalPersistence);
          logger.info('[Auth] Persistence set to browserLocalPersistence');

          // Check idle timeout if user is logged in
          if (firebaseUser && isSessionExpired()) {
            logger.info('[Auth] Session expired due to inactivity, signing out');
            await signOut(auth);
            // Redirect will happen via the route guards
            return; // onAuthStateChanged will fire again with null user
          }

          // Update activity timestamp if user is authenticated
          if (firebaseUser) {
            updateLastActivity();
          }
        } catch (error) {
          logger.error('[Auth] Failed to initialize auth:', error);
        }
      }

      setUser(firebaseUser);
      setIsLoading(false);

      // Update Sentry user context
      if (firebaseUser) {
        setSentryUser({ uid: firebaseUser.uid });
        flushPerformanceMetrics();
      } else {
        clearSentryUser();
      }
    });

    return unsubscribe;
  }, []);

  // ============================================================================
  // Activity Tracking - Reset idle timeout on user activity
  // ============================================================================
  useEffect(() => {
    if (!user) return; // Only track activity when logged in

    const handleActivity = () => {
      updateLastActivity();
    };

    // Track various user activities
    const events = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'];

    // Throttle activity updates to avoid excessive localStorage writes
    let lastUpdate = Date.now();
    const throttledActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        // Update at most once per minute
        lastUpdate = now;
        handleActivity();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    // Also track visibility changes (tab becomes visible)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  // ============================================================================
  // Periodic Idle Timeout Check
  // ============================================================================
  useEffect(() => {
    if (!user) return; // Only check when logged in

    const checkIdleTimeout = async () => {
      if (isSessionExpired()) {
        logger.info('[Auth] Idle timeout exceeded, signing out');
        await signOut(auth);
        // Redirect to login with reason
        window.location.href = '/login?reason=session_expired';
      }
    };

    const interval = setInterval(checkIdleTimeout, IDLE_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [user]);

  // Monitor token changes for analytics/Sentry (simplified - no manual refresh)
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        logger.info('[Auth] Token changed/refreshed');
        // Update activity on token refresh (indicates active session)
        updateLastActivity();
      }
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string, rememberMe = false): Promise<void> => {
    // Always use local persistence for cross-tab sharing
    await setPersistence(auth, browserLocalPersistence);
    await signInWithEmailAndPassword(auth, email, password);

    // Set remember me preference (controls idle timeout duration)
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true');
      logger.info('[Auth] Remember me enabled - idle timeout: 30 days');
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
      logger.info('[Auth] Remember me disabled - idle timeout: 12 hours');
    }

    // Update activity timestamp on login
    updateLastActivity();
  };

  const signup = async (email: string, password: string, displayName?: string): Promise<void> => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update the user's display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }
  };

  const logout = async (): Promise<void> => {
    await signOut(auth);
  };

  const resetPassword = async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  };

  /**
   * Helper to extract isNewUser from UserCredential
   */
  const extractOAuthResult = (
    credential: UserCredential,
    providerId: string
  ): OAuthSignInResult => {
    const additionalInfo = getAdditionalUserInfo(credential);
    const isNewUser = additionalInfo?.isNewUser ?? false;

    logger.info('[Auth] OAuth result:', {
      providerId,
      isNewUser,
      email: credential.user.email,
    });

    return {
      user: credential.user,
      isNewUser,
      providerId,
    };
  };

  // Google Sign-In with popup (fallback to redirect for Safari/blocked popups)
  // Returns isNewUser so caller can handle appropriately
  const signInWithGoogle = async (): Promise<OAuthSignInResult> => {
    // Use local persistence for cross-tab sharing
    await setPersistence(auth, browserLocalPersistence);
    // OAuth users get default idle timeout (no "remember me" option in OAuth flow)
    localStorage.removeItem(REMEMBER_ME_KEY);

    const provider = new GoogleAuthProvider();
    // Request email scope for user info
    provider.addScope('email');
    provider.addScope('profile');

    try {
      // Try popup first (works on most browsers)
      const credential = await signInWithPopup(auth, provider);
      logger.info('[Auth] Google sign-in successful via popup');
      return extractOAuthResult(credential, 'google.com');
    } catch (error) {
      // Check if popup was blocked or failed
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        (error.code === 'auth/popup-blocked' ||
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request')
      ) {
        // Fallback to redirect for Safari or when popup is blocked
        logger.info('[Auth] Popup blocked/closed, falling back to redirect');
        await signInWithRedirect(auth, provider);
        // For redirect, we can't return immediately - the page will reload
        // Return a placeholder that won't be used (redirect happens)
        throw new Error('REDIRECT_IN_PROGRESS');
      } else {
        // Re-throw other errors
        throw error;
      }
    }
  };

  // Microsoft Sign-In with popup (fallback to redirect for Safari/blocked popups)
  // Returns isNewUser so caller can handle appropriately
  const signInWithMicrosoft = async (): Promise<OAuthSignInResult> => {
    // Use local persistence for cross-tab sharing
    await setPersistence(auth, browserLocalPersistence);
    // OAuth users get default idle timeout (no "remember me" option in OAuth flow)
    localStorage.removeItem(REMEMBER_ME_KEY);

    const provider = new OAuthProvider('microsoft.com');
    // Request email scope for user info
    provider.addScope('email');
    provider.addScope('profile');

    try {
      // Try popup first (works on most browsers)
      const credential = await signInWithPopup(auth, provider);
      logger.info('[Auth] Microsoft sign-in successful via popup');
      return extractOAuthResult(credential, 'microsoft.com');
    } catch (error) {
      // Check if popup was blocked or failed
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        (error.code === 'auth/popup-blocked' ||
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request')
      ) {
        // Fallback to redirect for Safari or when popup is blocked
        logger.info('[Auth] Popup blocked/closed, falling back to redirect');
        await signInWithRedirect(auth, provider);
        // For redirect, we can't return immediately - the page will reload
        throw new Error('REDIRECT_IN_PROGRESS');
      } else {
        // Re-throw other errors
        throw error;
      }
    }
  };

  // Force refresh the auth token (useful before sensitive operations)
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    try {
      const token = await currentUser.getIdToken(true);
      logger.info('[Auth] Token manually refreshed');
      return token;
    } catch (error) {
      logger.error('[Auth] Failed to refresh token:', error);
      return null;
    }
  }, []);

  const isAuthenticated = !!user;

  const clearOAuthRedirectNewUser = useCallback(() => {
    setOauthRedirectNewUser(null);
    sessionStorage.removeItem('oauth_redirect_new_user');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        signup,
        logout,
        resetPassword,
        signInWithGoogle,
        signInWithMicrosoft,
        refreshToken,
        oauthRedirectNewUser,
        clearOAuthRedirectNewUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
